import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAuth } from './contexts/AuthContext';
import { Role } from '../../shared/types/user.types';
import { useTranslation } from 'react-i18next';
import MainLayout from './layouts/MainLayout';
import AppLayout from './layouts/AppLayout';
import ProfileLayout from './layouts/ProfileLayout';
import PlaceholderPage from './components/PlaceholderPage';
import { appStyles } from './App.styles';

// Public Pages
import Landing from './pages/public/Landing';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import StatusBlockPage from './pages/auth/StatusBlockPage';

// Common Pages
import Profile from './pages/common/Profile';
import Notifications from './pages/common/Notifications';

// Customer Pages
import Garage from './pages/customer/Garage';
import AddMachine from './pages/customer/AddMachine';
import CustomerVehicleDetail from './pages/customer/VehicleDetail';
import ServiceRequest from './pages/customer/ServiceRequest';
import CustomerServiceDetail from './pages/customer/ServiceDetail';
import CustomerTransferDetail from './pages/customer/TransferDetail';
import PrivacySettings from './pages/common/PrivacySettings';

// Dealer Pages
import ServiceDesk from './pages/dealer/ServiceDesk';
import DealerVehicleDetail from './pages/dealer/VehicleDetail';
import AddServiceRecord from './pages/dealer/AddServiceRecord';
import ServiceQueue from './pages/dealer/ServiceQueue';
import DealerServiceDetail from './pages/dealer/ServiceDetail';
import DealerGarageDetail from './pages/dealer/GarageDetail';
import DealerTransferDetail from './pages/dealer/TransferDetail';

// Admin Pages
import Dashboard from './pages/admin/Dashboard';
import VehicleRegistrationQueue from './pages/admin/VehicleRegistrationQueue';
import VehicleRegistrationDetail from './pages/admin/VehicleRegistrationDetail';
import TransferQueue from './pages/admin/TransferQueue';
import TransferDetail from './pages/admin/TransferDetail';
import AccountsList from './pages/admin/AccountsList';
import AccountDetail from './pages/admin/AccountDetail';
import CreateAccount from './pages/admin/CreateAccount';
import SettingListsItems from './pages/admin/SettingListsItems';
import AuditLog from './pages/admin/AuditLog';

// System Pages
import NotFound from './pages/system/NotFound';

const RequireRole: React.FC<{ role: Role; children: React.ReactNode }> = ({ role, children }) => {
    const { user, isAuthenticated } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (user?.role !== role && user?.role !== 'admin') { // admin má právo všude
        return (
            <Box sx={appStyles.accessDenied}>
                <Typography variant="h5" color="error" fontWeight={700}>{t('auth.accessDenied.title')}</Typography>
                <Typography color="text.secondary" mt={1}>{t('auth.accessDenied.message')}</Typography>
            </Box>
        );
    }

    return <>{children}</>;
};

const RoleRedirect: React.FC = () => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;

    switch (user.role) {
        case 'customer': return <Navigate to="/app/garage" replace />;
        case 'dealer': return <Navigate to="/app/service-desk" replace />;
        case 'admin': return <Navigate to="/app/admin/dashboard" replace />;
        default: return <Navigate to="/" replace />;
    }
};

function App() {
    const { t } = useTranslation();
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout />}>
                <Route index element={<Landing />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="about" element={<PlaceholderPage title={t('nav.about')} />} />
                <Route path="how-it-works" element={<PlaceholderPage title={t('nav.howItWorks')} />} />
                <Route path="support" element={<PlaceholderPage title={t('nav.support')} />} />
                <Route path="email-verification" element={<EmailVerificationPage />} />
                <Route path="account-status" element={<StatusBlockPage />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* App Routes (Authenticated) */}
            <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="/app/redirect" replace />} />
                <Route path="redirect" element={<RoleRedirect />} />

                <Route path="profile" element={<ProfileLayout />}>
                    <Route index element={<Profile />} />
                    <Route path="privacy-settings" element={<PrivacySettings />} />
                </Route>
                <Route path="notifications" element={<Notifications />} />

                {/* Customer Routes */}
                <Route path="garage" element={<RequireRole role="customer"><Garage /></RequireRole>} />
                <Route path="garage/add-machine" element={<RequireRole role="customer"><AddMachine /></RequireRole>} />
                <Route path="vehicles/:vin" element={<RequireRole role="customer"><CustomerVehicleDetail /></RequireRole>} />
                <Route path="vehicles/:vin/service-request" element={<RequireRole role="customer"><ServiceRequest /></RequireRole>} />
                <Route path="vehicles/:vin/services/:serviceId" element={<RequireRole role="customer"><CustomerServiceDetail /></RequireRole>} />
                <Route path="transfers/:id" element={<RequireRole role="customer"><CustomerTransferDetail /></RequireRole>} />

                {/* Dealer Routes */}
                <Route path="service-desk" element={<RequireRole role="dealer"><ServiceDesk /></RequireRole>} />
                <Route path="current-services" element={<RequireRole role="dealer"><ServiceQueue /></RequireRole>} />
                <Route path="service-desk/vehicles/:vin" element={<RequireRole role="dealer"><DealerVehicleDetail /></RequireRole>} />
                <Route path="service-desk/vehicles/:vin/add-service" element={<RequireRole role="dealer"><AddServiceRecord /></RequireRole>} />
                <Route path="service-desk/transfers/:id" element={<RequireRole role="dealer"><DealerTransferDetail /></RequireRole>} />
                <Route path="service-desk/customers/:uid" element={<RequireRole role="dealer"><DealerGarageDetail /></RequireRole>} />
                <Route path="service-desk/services/:id" element={<RequireRole role="dealer"><DealerServiceDetail /></RequireRole>} />

                {/* Admin Routes */}
                <Route path="admin/dashboard" element={<RequireRole role="admin"><Dashboard /></RequireRole>} />
                <Route path="admin/vehicle-registrations" element={<RequireRole role="admin"><VehicleRegistrationQueue /></RequireRole>} />
                <Route path="admin/vehicle-registrations/:uid" element={<RequireRole role="admin"><VehicleRegistrationDetail /></RequireRole>} />
                <Route path="admin/transfers" element={<RequireRole role="admin"><TransferQueue /></RequireRole>} />
                <Route path="admin/transfers/:id" element={<RequireRole role="admin"><TransferDetail /></RequireRole>} />
                <Route path="admin/users" element={<RequireRole role="admin"><AccountsList /></RequireRole>} />
                <Route path="admin/users/new" element={<RequireRole role="admin"><CreateAccount /></RequireRole>} />
                <Route path="admin/users/:uid" element={<RequireRole role="admin"><AccountDetail /></RequireRole>} />
                <Route path="admin/settings" element={<RequireRole role="admin"><SettingListsItems /></RequireRole>} />
                <Route path="admin/exports" element={<RequireRole role="admin"><PlaceholderPage title={t('nav.exports')} /></RequireRole>} />
                <Route path="admin/audit-log" element={<RequireRole role="admin"><AuditLog /></RequireRole>} />
            </Route>

            {/* System Routes */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

export default App;
